import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTACT_EMAIL = "contact@sylva-invest.com";
const CONTACT_PHONE = "+33681481237";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    type: "",
    message: "",
    consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent) {
      toast.error("Veuillez accepter d'être contacté concernant votre demande.");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        location: formData.location || null,
        user_type: formData.type || null,
        message: formData.message || null,
      });
      
      if (response.data.status === "success") {
        // Google Ads conversion tracking - triggered only on successful form submission
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-18451239215/lqH8CLm22vccEK-int5E',
            'value': 1.0,
            'currency': 'EUR'
          });
        }
        
        toast.success("Merci pour votre message. Nous vous recontacterons rapidement.");
        setFormData({
          name: "",
          email: "",
          phone: "",
          location: "",
          type: "",
          message: "",
          consent: false,
        });
      }
    } catch (error) {
      console.error("Error sending contact form:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer ou nous contacter directement par email.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-cream"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-sans font-semibold mb-4">
              Nous Contacter
            </p>
            <h2
              className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight text-forest mb-6"
              data-testid="contact-headline"
            >
              Discutons de Vos Actifs Forestiers
            </h2>
            <div className="section-divider mb-8" />

            <p className="font-sans text-base md:text-lg text-gray-700 leading-relaxed mb-8">
              Que vous cherchiez à vendre, acquérir, remembrer ou simplement
              comprendre vos options en matière de foncier forestier—nous sommes là pour vous accompagner
              dans vos projets de regroupement de parcelles et transactions forestières.
            </p>

            <div className="space-y-6">
              <div className="flex items-center">
                <Mail className="w-5 h-5 text-gold mr-4" />
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="font-sans text-forest hover:text-gold transition-colors"
                  data-testid="contact-email"
                >
                  {CONTACT_EMAIL}
                </a>
              </div>
              <div className="flex items-center">
                <Phone className="w-5 h-5 text-gold mr-4" />
                <a
                  href={`tel:${CONTACT_PHONE}`}
                  className="font-sans text-forest hover:text-gold transition-colors"
                  data-testid="contact-phone"
                >
                  {CONTACT_PHONE}
                </a>
              </div>
              <div className="flex items-center">
                <MapPin className="w-5 h-5 text-gold mr-4" />
                <span className="font-sans text-gray-700">
                  Charente / Nouvelle-Aquitaine, France
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Votre Nom *"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input w-full"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Adresse Email *"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input w-full"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Téléphone (optionnel)"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input w-full"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="location"
                    placeholder="Ville / Département"
                    value={formData.location}
                    onChange={handleChange}
                    className="form-input w-full"
                    data-testid="input-location"
                  />
                </div>
              </div>

              <div>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="form-input w-full cursor-pointer"
                  data-testid="select-type"
                >
                  <option value="">Je suis...</option>
                  <option value="owner">Propriétaire forestier</option>
                  <option value="heir">Héritier / Membre de famille</option>
                  <option value="investor">Investisseur</option>
                  <option value="professional">Professionnel (Notaire, etc.)</option>
                  <option value="other">Autre</option>
                </select>
              </div>

              <div>
                <textarea
                  name="message"
                  placeholder="Votre Message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="form-input w-full resize-none"
                  data-testid="input-message"
                />
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  name="consent"
                  id="consent"
                  checked={formData.consent}
                  onChange={handleChange}
                  className="custom-checkbox mt-1 mr-3"
                  data-testid="checkbox-consent"
                />
                <label htmlFor="consent" className="font-sans text-sm text-gray-600 cursor-pointer">
                  J'accepte d'être contacté concernant ma demande.
                </label>
              </div>

              <button
                type="submit"
                className="btn-primary w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Envoi en cours..." : "Envoyer le Message"}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
